import { createPayerIdentityMetadata } from 'actions/entities/orders/metadata';
import { fetchSender } from 'actions/entities/sender/sender';
import { taxErrorNotification } from 'actions/errors/errors';
import { enableFeature } from 'actions/features/features';
import { trackCouponValidation } from 'actions/tracker/tracker';
import { setFieldValue } from 'actions/ui/ui';
import { PERMANENT_COOKIE_NAME } from 'constants/cookies';
import { COUPON_CODE } from 'constants/coupons';
import { INVALID_PARAM_ERROR } from 'constants/errors';
import { MAINTENANCE_MODE } from 'constants/features';
import { PAYER_IDENTITY_FIELD } from 'constants/fields';
import { HTTPStatuses, type HttpError } from 'constants/http';
import {
  ADD_COUPON_FAILURE,
  ADD_COUPON_REQUEST,
  ADD_ORDER_TO_USER_FAILURE,
  ADD_ORDER_TO_USER_REQUEST,
  ADD_ORDER_TO_USER_SUCCESS,
  ADD_REDEEM_REWARDS_FAILURE,
  ADD_REDEEM_REWARDS_REQUEST,
  CREATE_ORDER_FAILURE,
  CREATE_ORDER_REQUEST,
  CREATE_ORDER_SUCCESS,
  DELETE_COUPON_FAILURE,
  DELETE_COUPON_REQUEST,
  DELETE_REDEEM_REWARDS_FAILURE,
  DELETE_REDEEM_REWARDS_REQUEST,
  EMPTY_ERROR_FIELDS,
  RESET_ORDER,
  UPDATE_ORDER_FAILURE,
  UPDATE_ORDER_REQUEST,
  UPDATE_ORDER_SUCCESS,
} from 'constants/orders';
import { schemas } from 'constants/schemas';
import { PAYER_IDENTITY_OTHER } from 'constants/ui';
import type { Decamelized } from 'humps';
import { decamelizeKeys } from 'humps';
import isEmpty from 'lodash/isEmpty';
import type { Field, FieldEntityType } from 'models/field';
import type { Item } from 'models/item';
import type { Order } from 'models/order';
import type { Price } from 'models/price';
import { normalize } from 'normalizr';
import type { NormalizedEntity, RootState } from 'reducers/types';
import type { Dispatch } from 'redux';
import {
  getFieldByEntity,
  getFieldById,
} from 'selectors/entities/fields/fields';
import { getItems } from 'selectors/entities/items/items';
import {
  getOrder,
  getOrderOffer,
  getOrderSectionFieldValue,
  isCouponApplied,
  isReadOnlyOrderSectionField,
  isRedeemRewardsApplied,
} from 'selectors/entities/order/order';
import { getPayouts } from 'selectors/entities/payouts/payouts';
import { getRecipient } from 'selectors/entities/recipient/recipient';
import { isThereAnErrorRetrievingTaxes } from 'selectors/errors/errors';
import { getPartnerModalFields } from 'selectors/ui/modals/modals';
import {
  getCouponCode,
  getCurrentLocale,
  getFieldValue,
  isEmbedded,
  isFetching,
  shouldHideUserManagement,
} from 'selectors/ui/ui';
import type {
  CreateOrderRequestBody,
  OfferEntityFields,
  OrderRequestBody,
  OrderServiceError,
  OwnershipResponse,
  RecipientEntityFields,
} from 'services/apiV3/orders/types';
import { extraFees } from 'services/payex/extraFees/extraFees';
import type { PayexDispatch, PayexThunkAction } from 'store/configureStore';
import { cookies } from 'utils/cookies/cookies';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import { url } from 'utils/url/url';

type APIError = {
  parsedBody?: { errors?: OrderServiceError[] };
  status: number;
};

const ORDER_CREATION_ENTITIES: FieldEntityType[] = ['sender', 'recipient'];

const alreadyFetchingOrder = () =>
  Promise.reject(new Error('Already fetching order...'));

const hasOrderValueChanged = (field: Field, state: RootState) => {
  const orderFieldValue = getOrderSectionFieldValue(field.id, state);

  if (!orderFieldValue) {
    return !!field.defaultValue;
  }

  return field.defaultValue !== orderFieldValue;
};

const editableFields = (field: Field) => !field.apiReadOnly;

const normalizeEntity = (
  fields: NormalizedEntity<Field>,
  state: RootState,
  recreateOrder = false,
) => {
  const getFieldsById = (fieldId: string) => getFieldById(state, fieldId);

  const filledFields = (field: Field) =>
    hasOrderValueChanged(field, state) || !!field.defaultValue;

  const normalizeFields = (field: Field) => ({
    id: field.name,
    value: field.defaultValue,
  });
  let normalizedFields = fields.ids.map(getFieldsById).filter(filledFields);

  if (!recreateOrder) {
    normalizedFields = normalizedFields.filter(editableFields);
  }

  const finalFields = normalizedFields.map(normalizeFields);

  return finalFields.length ? { fields: finalFields } : {};
};

export const entitiesPayload = (
  entityOrEntities: FieldEntityType | FieldEntityType[],
  state: RootState,
  isUpdate = false,
  recreateOrder = false,
) => {
  const fingerprint = cookies.read(PERMANENT_COOKIE_NAME) || undefined;
  const entities = Array.isArray(entityOrEntities)
    ? entityOrEntities
    : new Array(entityOrEntities);

  const payload = entities.reduce((acc, entity) => {
    const fields = getFieldByEntity(state, entity);

    const normalizedPayload = normalizeEntity(fields, state, recreateOrder);

    if (entity === 'sender') {
      if (!isUpdate || !isReadOnlyOrderSectionField(state, 'sender_country')) {
        const selectedCountry = getFieldValue(state, 'sender_country');
        const countryField = { id: 'country', value: selectedCountry };
        const { fields = [] } = normalizedPayload;
        normalizedPayload.fields = [...fields, countryField];
      }

      const areTermsAccepted = !!getFieldValue(state, 'are_terms_accepted');
      if (areTermsAccepted) {
        const termsField = {
          id: 'are_terms_accepted',
          value: areTermsAccepted,
        };
        normalizedPayload.fields = normalizedPayload.fields || [];
        normalizedPayload.fields.push(termsField);
      }
    }

    if (entity === 'offers') {
      const offerValue = getFieldValue(state, 'offer');
      (normalizedPayload as OfferEntityFields).id = offerValue as string;
    }

    if (entity === 'recipient') {
      const items = recreateOrder
        ? getItems(state)
        : getItems(state).filter(editableFields);

      const itemsPayload = items.map(item => {
        return { id: item.id, amount: Number(getFieldValue(state, item.id)) };
      });

      if (itemsPayload.length) {
        (normalizedPayload as RecipientEntityFields).items = itemsPayload;
      }
    }

    return {
      ...acc,
      ...{ [entity === 'offers' ? 'offer' : entity]: normalizedPayload },
      fingerprint,
    };
  }, {} as OrderRequestBody);

  const locale = getCurrentLocale(state);
  const provider = getFieldValue(state, 'provider');
  if (provider) {
    payload.provider = provider as string;
  }

  if (isEmbedded(state)) {
    const callbackUrl = getFieldValue(state, 'callback_url');
    const callbackVersion = getFieldValue(state, 'callback_version');

    const hasCallbackUrl = callbackUrl && (callbackUrl as string).length > 0;
    const isCallbackV2 = callbackVersion === '2';

    const statusChangeCallback = {
      id: getFieldValue(state, 'callback_id') as string,
      ...(hasCallbackUrl ? { url: callbackUrl as string } : {}),
      ...(isCallbackV2 ? { version: '2' } : {}),
    };

    payload.callback = statusChangeCallback;
  }

  return {
    ...payload,
    locale,
  };
};

const unexpectedError = (error: APIError, dispatch: PayexDispatch) => {
  dispatch({ type: CREATE_ORDER_FAILURE, payload: {} });
  throw error;
};

const paymentInformationError = (
  error: { parsedBody?: { errors?: OrderServiceError[] } },
  dispatch: PayexDispatch,
) => {
  const { parsedBody: { errors } = {} } = error;
  const payload = normalize(errors, schemas.arrayOfErrors);

  dispatch({ type: CREATE_ORDER_FAILURE, payload });
  throw error;
};

const removeInvalidFieldsFromPayload = (
  errors: OrderServiceError[] = [],
  payload: CreateOrderRequestBody,
) => {
  const newPayload = { ...payload };
  const COUNTRY_SOURCE = '/sender/fields/country';
  const { sender, recipient, offer } = newPayload;

  const omitCountry = (error: OrderServiceError) =>
    error.source !== COUNTRY_SOURCE;
  const fieldsToRemove = errors.filter(omitCountry).map(error => error.param);

  const isValid = (field: Field) => !fieldsToRemove.includes(field.id);
  const newSenderFields = sender.fields.filter(isValid);
  const newRecipientFields = recipient.fields
    ? recipient.fields.filter(isValid)
    : [];
  const newOfferFields = offer?.fields ? offer.fields.filter(isValid) : [];

  newPayload.sender.fields = newSenderFields;
  newPayload.recipient.fields = newRecipientFields;
  if (newPayload.offer && !isEmpty(offer)) {
    newPayload.offer.fields = newOfferFields;
  }

  return newPayload;
};

const createOrderRetry =
  (error: APIError, payload: CreateOrderRequestBody): PayexThunkAction =>
  (dispatch, _getState, { apiV3 }) => {
    const { parsedBody: { errors } = {} } = error;

    dispatch({
      type: EMPTY_ERROR_FIELDS,
      payload: normalize(errors, schemas.arrayOfErrors),
    });

    return apiV3.orders
      .create(removeInvalidFieldsFromPayload(errors, payload))
      .then(response => {
        dispatch({ type: CREATE_ORDER_SUCCESS, payload: response });
      })
      .catch(error => {
        unexpectedError(error, dispatch);
      });
  };

export const createOrder =
  (
    recreateOrder = false,
  ): PayexThunkAction<Promise<void | { type: string; payload: unknown }>> =>
  (dispatch, getState, { apiV3 }) => {
    const state = getState();
    const isOfferSelected = !!getFieldValue(state, 'offer');
    const isRecurringConfigurationSet = !!getFieldValue(
      state,
      'metadata_recurring_configuration_id',
    );
    const orderCreationEntities = [...ORDER_CREATION_ENTITIES];
    if (isOfferSelected) {
      orderCreationEntities.push('offers');
    }

    const payload = entitiesPayload(
      orderCreationEntities,
      state,
      false,
      recreateOrder,
    );

    const payouts = getPayouts(state);
    if (!isEmpty(payouts)) {
      payload.payables = payouts;
    }

    const recipientId = 'recipient';
    if (payload.recipient) {
      payload.recipient.id = getFieldValue(state, recipientId) as string;
    }

    const callbackId = getFieldValue(state, 'callback_id');
    const callbackUrl = getFieldValue(state, 'callback_url');
    const callbackVersion = getFieldValue(state, 'callback_version');

    if (callbackId || callbackUrl || callbackVersion) {
      payload.callback = {
        ...(callbackId && { id: callbackId as string }),
        ...(callbackUrl && { url: callbackUrl as string }),
        ...(callbackVersion && { version: callbackVersion as string }),
      };
    }

    const returnUrl = getFieldValue(state, 'return_url');
    if (returnUrl) {
      payload.return_url = returnUrl as string;
    }

    payload.metadata = payload.metadata || {};

    if (recreateOrder) {
      const currentOrder = getOrder(state);
      if (currentOrder?.metadata) {
        const { trackingUrl: _trackingUrl, ...restMetadata } =
          currentOrder.metadata;
        payload.metadata = {
          ...payload.metadata,
          ...(decamelizeKeys(restMetadata) as Record<string, string>),
        };
      }
    }

    if (isRecurringConfigurationSet) {
      payload.metadata = {
        ...payload.metadata,
        recurring_configuration_id: getFieldValue(
          state,
          'metadata_recurring_configuration_id',
        ) as string,
      };
    }

    const payerIdentity = getFieldValue(state, PAYER_IDENTITY_FIELD) as
      | string
      | undefined;
    if (payerIdentity) {
      const partnerFields =
        payerIdentity === PAYER_IDENTITY_OTHER
          ? getPartnerModalFields(state)
          : {};

      const payerIdentityMetadata = createPayerIdentityMetadata(
        payerIdentity,
        partnerFields,
      );

      if (Object.keys(payerIdentityMetadata).length > 0) {
        payload.metadata = {
          ...payload.metadata,
          ...payerIdentityMetadata,
        };
      }
    }

    dispatch({ type: CREATE_ORDER_REQUEST });

    return apiV3.orders
      .create(payload)
      .then(response =>
        dispatch({ type: CREATE_ORDER_SUCCESS, payload: response }),
      )
      .catch((error: APIError) => {
        if (error.status === 503) {
          return dispatch(enableFeature(MAINTENANCE_MODE));
        }

        const { parsedBody: { errors } = {} } = error;
        const isValidationError = (error: OrderServiceError) =>
          error.type === INVALID_PARAM_ERROR;
        const validationErrors = errors?.filter(isValidationError);
        const hasNotValidationErrors = validationErrors?.length === 0;

        const isRecipient = (error: OrderServiceError) =>
          error.param === 'recipient';
        const recipientError = validationErrors?.find(isRecipient);
        if (recipientError) {
          dispatch({ type: CREATE_ORDER_FAILURE, payload: recipientError });
          throw error;
        }

        const paymentInformationFields = ['country', 'items', 'amount'];
        const isPaymentInformation = (error: OrderServiceError) =>
          error.param && paymentInformationFields.includes(error.param);
        const paymentInformationErrors =
          validationErrors?.filter(isPaymentInformation);

        const hasPaymentInformationErrors = !!paymentInformationErrors?.length;

        if (hasNotValidationErrors) {
          return unexpectedError(error, dispatch);
        }

        if (hasPaymentInformationErrors) {
          return paymentInformationError(error, dispatch);
        }

        return dispatch(createOrderRetry(error, payload));
      });
  };

const returnUrl = (id: string, token: string, hideUserManagement: boolean) => {
  const domain = url.getCurrentUrl();
  let trackingUrl = `${domain}/tracking/${id}?token=${token}`;

  if (hideUserManagement === true) {
    trackingUrl = `${trackingUrl}&hideUserManagement=true`;
  }

  return trackingUrl;
};

export const updateOrderMetadata =
  (metadata: Record<string, string>): PayexThunkAction =>
  (dispatch, getState) => {
    const state = getState();
    const provider = getFieldValue(state, 'provider');

    return dispatch(
      updateOrder({
        ...(provider && { provider: provider as string }),
        fingerprint: cookies.read(PERMANENT_COOKIE_NAME) || undefined,
        metadata,
      }),
    );
  };

export const addExtraFees =
  () => (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const order = getOrder(state);
    const offer = getOrderOffer(state);

    const currency = (offer.price as Price).currency;

    order.price = {
      currency: { code: currency },
    } as Price;

    const requestBody = decamelizeKeys(order) as Decamelized<Order>;

    dispatch({ type: UPDATE_ORDER_REQUEST });

    extraFees
      .add(requestBody)
      .then(response => {
        dispatch({ type: UPDATE_ORDER_SUCCESS, payload: response });
      })
      .catch(error => {
        const { parsedBody: { errors: validationErrors = [] } = {} } = error;

        dispatch({
          type: UPDATE_ORDER_FAILURE,
          payload: normalize(validationErrors, schemas.arrayOfErrors),
        });
      });
  };

export const updateOrder =
  (
    payload: Partial<OrderRequestBody>,
    shouldRetryWithInvalidFields = false,
  ): PayexThunkAction<Promise<unknown>> =>
  async (dispatch, getState, { apiV3 }) => {
    const state = getState();
    const isFetchingOrder = isFetching(state, 'order');

    if (isFetchingOrder) {
      return alreadyFetchingOrder();
    }

    dispatch({ type: UPDATE_ORDER_REQUEST });

    const { id, token } = getOrder(state);
    const hideUserManagement = shouldHideUserManagement(state);
    payload.return_url = returnUrl(id, token, hideUserManagement);
    payload.metadata = {
      ...payload.metadata,
      tracking_url: returnUrl(id, token, hideUserManagement),
    };

    const recipient = getRecipient(state);

    try {
      const response = await apiV3.orders.update({
        orderId: id,
        token,
        ...payload,
        recipientId: recipient?.id,
      });
      dispatch({ type: UPDATE_ORDER_SUCCESS, payload: response });
      return response;
    } catch (error: unknown) {
      const httpError = error as HttpError;
      if (httpError.status === 503) {
        return dispatch(enableFeature(MAINTENANCE_MODE));
      }

      const { parsedBody: { errors: validationErrors = [] } = {} } = httpError;

      httpError.message = `${UPDATE_ORDER_FAILURE} ${httpError.message}`;
      if (
        httpError.status &&
        httpError.status >= HTTPStatuses.INTERNAL_SERVER_ERROR
      ) {
        errorNotifier.notifyWithFingerprint(httpError, [
          UPDATE_ORDER_FAILURE,
          httpError.status as unknown as string,
        ]);
      }

      dispatch({
        type: UPDATE_ORDER_FAILURE,
        payload: normalize(validationErrors, schemas.arrayOfErrors),
      });

      if (isThereAnErrorRetrievingTaxes(getState())) {
        dispatch(taxErrorNotification());
      }

      // Retry with first state and dob fixes if enabled and no blocking errors
      if (shouldRetryWithInvalidFields && !hasBlockingErrors(error)) {
        return retryUpdateOrderWithFixes(payload, error, dispatch, getState);
      }

      throw error;
    }
  };

export const updateOrderWithEntities =
  (
    entities: FieldEntityType | FieldEntityType[],
    shouldRetryWithInvalidFields = false,
  ): PayexThunkAction<Promise<unknown>> =>
  (dispatch, getState) => {
    const state = getState();
    const payload = entitiesPayload(entities, state, true);

    return dispatch(updateOrder(payload, shouldRetryWithInvalidFields));
  };

const paymentInformationPayload = (state: RootState) => {
  const locale = getCurrentLocale(state);

  const fingerprint = cookies.read(PERMANENT_COOKIE_NAME);

  const sender: { fields: { id: string; value: string }[] } = {
    fields: [],
  };
  if (!isReadOnlyOrderSectionField(state, 'sender_country')) {
    const selectedCountry = getFieldValue(state, 'sender_country') as string;
    sender.fields.push({ id: 'country', value: selectedCountry });
  }

  const formatItem = (item: Item) => {
    const { id } = item;
    const amount = Number(getFieldValue(state, item.id));

    return { id, amount };
  };
  const items = getItems(state);
  const itemsPayload = items.filter(editableFields).map(formatItem);
  const recipient = { items: itemsPayload };

  return { sender, recipient, locale, fingerprint };
};

const STATE_FIELD = 'state';
const DOB_FIELD = 'date_of_birth';
const DEFAULT_DOB = '1999-01-01';

/**
 *
 * @param e exception
 * @returns Checks if the only errors in the response are state or DOB to decide whether to block the user from continuing in the payment creation process
 */
const hasBlockingErrors = (e: unknown) => {
  const errors = (e as HttpError).parsedBody?.errors as {
    param: string;
  }[];

  const errorsWithoutStateOrDob = errors.filter(
    error => error.param !== STATE_FIELD && error.param !== DOB_FIELD,
  );

  return errorsWithoutStateOrDob.length > 0;
};

/**
 * Retries updating the order with fixes for state and/or DOB errors.
 * This method only runs when a country requires a mandatory state or DOB, but the value in the order is not valid.
 * To workaround this issue, we update the order with:
 * - First state option from the field (for state errors)
 * - Static DOB of 01/01/1999 (for DOB errors)
 * So the user can later edit it in the Sender information page
 * @returns Promise that resolves when the retry update completes
 */
const retryUpdateOrderWithFixes = async (
  payload: Partial<OrderRequestBody> & {
    sender?: OrderRequestBody['sender'];
  },
  error: unknown,
  dispatch: PayexDispatch,
  getState: () => RootState,
): Promise<unknown> => {
  const httpError = error as HttpError;
  const errors = Array.isArray(httpError.parsedBody?.errors)
    ? (httpError.parsedBody.errors as OrderServiceError[])
    : [];

  const hasStateError = errors.some(
    (error: OrderServiceError) => error.param === STATE_FIELD,
  );
  const hasDobError = errors.some(
    (error: OrderServiceError) => error.param === DOB_FIELD,
  );

  if (hasStateError) {
    await dispatch(fetchSender());
  }

  const currentState = getState();
  const retryFields: { id: string; value: string }[] = [];

  if (hasStateError) {
    const senderState = currentState.entities.fields.sender.byId.sender_state;
    const firstState = Array.isArray(senderState.value)
      ? senderState.value[0].value
      : senderState.value;
    if (firstState) {
      retryFields.push({ id: STATE_FIELD, value: firstState as string });
    }
  }

  if (hasDobError) {
    retryFields.push({ id: DOB_FIELD, value: DEFAULT_DOB });
  }

  const retryPayload = {
    ...payload,
    sender: {
      ...(payload.sender ?? {}),
      fields: [...(payload.sender?.fields || []), ...retryFields],
    },
  };

  try {
    return await dispatch(updateOrder(retryPayload, false));
  } catch (retryError: unknown) {
    const retryHttpError = retryError as HttpError;
    errorNotifier.notify(retryHttpError);
    const retryErrors = Array.isArray(retryHttpError.parsedBody?.errors)
      ? (retryHttpError.parsedBody.errors as OrderServiceError[])
      : [];
    const stateError = retryErrors.find(
      (error: OrderServiceError) => error.param === STATE_FIELD,
    );
    const dobError = retryErrors.find(
      (error: OrderServiceError) => error.param === DOB_FIELD,
    );

    const errorsToDispatch: OrderServiceError[] = [...retryErrors];

    if (stateError) {
      errorsToDispatch.push({
        ...stateError,
        type: 'read_only',
      });
    }

    if (dobError) {
      errorsToDispatch.push({
        ...dobError,
        type: 'read_only',
      });
    }

    if (stateError || dobError) {
      dispatch({
        type: UPDATE_ORDER_FAILURE,
        payload: normalize(errorsToDispatch, schemas.arrayOfErrors),
      });
    }

    throw retryError;
  }
};

export const updateOrderWithPaymentInformation =
  (): PayexThunkAction<Promise<void>> => async (dispatch, getState) => {
    const state = getState();
    const payload = paymentInformationPayload(state);

    await dispatch(updateOrder(payload, true));
  };

export const addOrderToUser =
  (): PayexThunkAction<Promise<void | OwnershipResponse>> =>
  (dispatch, getState, { apiV3 }) => {
    const state = getState();
    const order = getOrder(state);
    const { id: orderId, token } = order;

    dispatch({ type: ADD_ORDER_TO_USER_REQUEST });

    return apiV3.orders
      .addOrderToUser({ orderId, token })
      .then(payload => dispatch({ type: ADD_ORDER_TO_USER_SUCCESS, payload }))
      .then(response => response.payload)
      .catch(error => {
        error.message = `${ADD_ORDER_TO_USER_FAILURE} ${error.message}`;
        if (error.status !== HTTPStatuses.UNPROCESSABLE_ENTITY) {
          errorNotifier.notifyWithFingerprint(error, [
            ADD_ORDER_TO_USER_FAILURE,
            error.status,
          ]);
        }
      });
  };

export const resetOrder = (): PayexThunkAction => dispatch => {
  dispatch({
    type: RESET_ORDER,
  });
};

export const addCoupon =
  (couponCode: string): PayexThunkAction<Promise<void>> =>
  (dispatch, getState, { apiV3 }) => {
    const state = getState();
    const isFetchingOrder = isFetching(state, 'order');

    if (isFetchingOrder) {
      return alreadyFetchingOrder();
    }

    dispatch({ type: ADD_COUPON_REQUEST });

    const { id, token } = getOrder(state);

    return apiV3.orders
      .addCoupon({
        orderId: id,
        token,
        couponCode,
      })
      .then(response => {
        const couponValidation = response?.couponValidation;
        delete response.couponValidation;

        dispatch({ type: UPDATE_ORDER_SUCCESS, payload: response });
        dispatch(setFieldValue(COUPON_CODE, couponCode));
        couponValidation && dispatch(trackCouponValidation(couponValidation));
      })
      .catch(error => {
        dispatch({ type: ADD_COUPON_FAILURE });

        error.message = `${ADD_COUPON_FAILURE} ${error.message}`;
        if (error.status > HTTPStatuses.UNPROCESSABLE_ENTITY) {
          errorNotifier.notifyWithFingerprint(error, [
            ADD_COUPON_FAILURE,
            error.status,
          ]);
        }

        const couponValidation = error.parsedBody?.couponValidation;
        couponValidation && dispatch(trackCouponValidation(couponValidation));

        throw error;
      });
  };

export const deleteCoupon =
  (couponCode: string): PayexThunkAction<Promise<void>> =>
  (dispatch, getState, { apiV3 }) => {
    const state = getState();
    const isFetchingOrder = isFetching(state, 'order');

    if (isFetchingOrder) {
      return alreadyFetchingOrder();
    }

    dispatch({ type: DELETE_COUPON_REQUEST });

    const { id, token } = getOrder(state);

    return apiV3.orders
      .deleteCoupon({
        orderId: id,
        token,
        couponCode,
      })
      .then(response => {
        dispatch({ type: UPDATE_ORDER_SUCCESS, payload: response });
        dispatch(setFieldValue(COUPON_CODE, ''));
      })
      .catch(error => {
        dispatch({ type: DELETE_COUPON_FAILURE });

        error.message = `${DELETE_COUPON_FAILURE} ${error.message}`;
        if (error.status >= HTTPStatuses.UNPROCESSABLE_ENTITY) {
          errorNotifier.notifyWithFingerprint(error, [
            DELETE_COUPON_FAILURE,
            error.status,
          ]);
        }

        throw error;
      });
  };

export const addRedeemRewards =
  (): PayexThunkAction<Promise<void>> =>
  (dispatch, getState, { apiV3 }) => {
    const state = getState();
    const isFetchingOrder = isFetching(state, 'order');

    if (isFetchingOrder) {
      return alreadyFetchingOrder();
    }

    dispatch({ type: ADD_REDEEM_REWARDS_REQUEST });

    const { id, token } = getOrder(state);

    return apiV3.orders
      .addRedeemRewards({
        orderId: id,
        token,
      })
      .then(response => {
        dispatch({ type: UPDATE_ORDER_SUCCESS, payload: response });
      })
      .catch(error => {
        dispatch({ type: ADD_REDEEM_REWARDS_FAILURE });

        error.message = `${ADD_REDEEM_REWARDS_FAILURE} ${error.message}`;
        if (error.status >= HTTPStatuses.UNPROCESSABLE_ENTITY) {
          errorNotifier.notifyWithFingerprint(error, [
            ADD_REDEEM_REWARDS_FAILURE,
            error.status,
          ]);
        }

        throw error;
      });
  };

export const deleteRedeemRewards =
  (): PayexThunkAction<Promise<void>> =>
  (dispatch, getState, { apiV3 }) => {
    const state = getState();
    const isFetchingOrder = isFetching(state, 'order');

    if (isFetchingOrder) {
      return alreadyFetchingOrder();
    }

    dispatch({ type: DELETE_REDEEM_REWARDS_REQUEST });

    const { id, token } = getOrder(state);

    return apiV3.orders
      .deleteRedeemRewards({
        orderId: id,
        token,
      })
      .then(response => {
        dispatch({ type: UPDATE_ORDER_SUCCESS, payload: response });
      })
      .catch(error => {
        dispatch({ type: DELETE_REDEEM_REWARDS_FAILURE });

        error.message = `${DELETE_REDEEM_REWARDS_FAILURE} ${error.message}`;
        if (error.status >= HTTPStatuses.UNPROCESSABLE_ENTITY) {
          errorNotifier.notifyWithFingerprint(error, [
            DELETE_REDEEM_REWARDS_FAILURE,
            error.status,
          ]);
        }
      });
  };

export const removeExtraFeesWithDiscount =
  (): PayexThunkAction<Promise<void>> => (dispatch, getState) => {
    const state = getState();
    const redeemRewardsApplied = isRedeemRewardsApplied(state);
    if (redeemRewardsApplied) {
      dispatch(deleteRedeemRewards());
    }

    const couponApplied = isCouponApplied(state);
    if (couponApplied) {
      const couponCode = getCouponCode(state);
      dispatch(deleteCoupon(couponCode as string));
    }

    return Promise.resolve();
  };
